<script>
    import { Check, Copy, X, Tag } from "@lucide/vue";

    const DISMISS_DURATION_MS = 1000 * 60 * 60 * 2;

    export default {
        components: {
            Check,
            Copy,
            X,
            Tag,
        },
        props: {
            config: {
                type: Object,
                default: null,
            },
        },
        data() {
            return {
                open: false,
                copied: false,
                keyHandler: null,
                openTimer: null,
                copyTimer: null,
            };
        },
        computed: {
            source() {
                return this.config?.source ?? "default";
            },
            storageKey() {
                return `kb_promo_popup_dismissed_at_${this.source}`;
            },
            badge() {
                return this.config?.badge ?? "Tam Finanslılara Özel Avantajlar";
            },
            coupon() {
                return this.config?.coupon ?? "";
            },
            discountPercent() {
                return this.config?.discount_percent ?? 10;
            },
            usesDismissStorage() {
                return this.source !== "param";
            },
        },
        mounted() {
            if (!this.config) return;
            if (typeof window === "undefined") return;

            if (this.usesDismissStorage) {
                try {
                    const last = Number(localStorage.getItem(this.storageKey) || 0);
                    if (Date.now() - last < DISMISS_DURATION_MS) return;
                } catch (e) {}
            }

            this.openTimer = setTimeout(() => {
                this.open = true;
            }, 1500);
        },
        beforeUnmount() {
            this.cleanup();
            if (this.openTimer) clearTimeout(this.openTimer);
            if (this.copyTimer) clearTimeout(this.copyTimer);
        },
        watch: {
            open(value) {
                if (value) {
                    this.keyHandler = (e) => {
                        if (e.key === "Escape") this.close();
                    };
                    document.addEventListener("keydown", this.keyHandler);
                    document.body.style.overflow = "hidden";
                } else {
                    this.cleanup();
                }
            },
        },
        methods: {
            cleanup() {
                if (this.keyHandler) {
                    document.removeEventListener("keydown", this.keyHandler);
                    this.keyHandler = null;
                }
                document.body.style.overflow = "";
            },
            close() {
                this.open = false;
                if (!this.usesDismissStorage) return;
                try {
                    localStorage.setItem(this.storageKey, String(Date.now()));
                } catch (e) {}
            },
            async copyCoupon() {
                try {
                    await navigator.clipboard.writeText(this.coupon);
                } catch (e) {
                    const ta = document.createElement("textarea");
                    ta.value = this.coupon;
                    document.body.appendChild(ta);
                    ta.select();
                    try {
                        document.execCommand("copy");
                    } catch (err) {}
                    document.body.removeChild(ta);
                }
                this.copied = true;
                if (this.copyTimer) clearTimeout(this.copyTimer);
                this.copyTimer = setTimeout(() => {
                    this.copied = false;
                }, 2000);
            },
        },
    };
</script>

<template>
    <Transition enter-active-class="transition-opacity duration-200 ease-out" enter-from-class="opacity-0" enter-to-class="opacity-100" leave-active-class="transition-opacity duration-150 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
        <div v-if="config && open" class="fixed inset-0 z-[100]" role="dialog" aria-modal="true" aria-labelledby="promo-title">
            <div class="absolute inset-0 bg-foreground/60 backdrop-blur-sm" @click="close" />

            <div class="fixed inset-0 flex items-center justify-center p-4 pointer-events-none">
                <Transition
                    appear
                    enter-active-class="transition duration-300 ease-out"
                    enter-from-class="opacity-0 scale-95 translate-y-4"
                    enter-to-class="opacity-100 scale-100 translate-y-0"
                    leave-active-class="transition duration-200 ease-in"
                    leave-from-class="opacity-100 scale-100 translate-y-0"
                    leave-to-class="opacity-0 scale-95 translate-y-4"
                >
                    <div v-if="open" class="relative w-full max-w-md overflow-hidden rounded-2xl bg-card shadow-2xl pointer-events-auto" style="user-select: none">
                        <!-- Header band -->
                        <div class="relative bg-muted py-4 px-5 text-foreground">
                            <button type="button" aria-label="Kapat" class="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full bg-foreground/10 text-foreground transition hover:bg-foreground/20" @click="close">
                                <X class="h-4 w-4" />
                            </button>
                            <div class="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground font-santralextrabold">
                                <Tag class="h-5 w-5" />
                                {{ badge }}
                            </div>
                        </div>

                        <!-- Body -->
                        <div class="bg-card px-6 pt-5 pb-6">
                            <p id="promo-title" class="text-[15px] font-semibold leading-snug text-foreground">Teknoloji ürünlerini kiralayarak sermayenizi koruyun, her zaman güncel teknolojiye sahip olun.</p>
                            <p class="mt-3 text-sm leading-tight text-muted-foreground font-santralregular">
                                Kiralabunu.com üzerindeki tüm teknoloji ürünlerinde
                                <span class="font-semibold text-foreground">%{{ discountPercent }} indirimin</span>
                                var. Beğendiğin ürünü seç, sepetine ekle, kupon kodunu kullan, kiralamanı tamamla.
                            </p>

                            <!-- Coupon -->
                            <div class="mt-4 flex items-stretch gap-2 rounded-xl border-2 border-dashed border-kbgreen bg-accent p-2">
                                <div class="flex flex-1 items-center justify-center">
                                    <span class="select-all font-mono text-lg font-bold tracking-[0.2em] text-foreground" style="user-select: all">
                                        {{ coupon }}
                                    </span>
                                </div>
                                <button type="button" class="inline-flex items-center gap-1.5 rounded-xl bg-kbgreen px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:opacity-90 active:scale-[0.98] font-santralregular" @click="copyCoupon">
                                    <template v-if="copied">
                                        <Check class="h-4 w-4" />
                                        Kopyalandı
                                    </template>
                                    <template v-else>
                                        <Copy class="h-4 w-4" />
                                        Kopyala
                                    </template>
                                </button>
                            </div>

                            <p class="mt-4 text-xs leading-relaxed text-muted-foreground font-santralregular">
                                Kira süren boyunca aylık ödemelerinin
                                <span class="font-semibold text-foreground">her ayında %{{ discountPercent }} indirim</span>
                                kazan.
                            </p>

                            <button target="_blank" rel="noopener noreferrer" class="mt-5 block w-full rounded-xl bg-kbgreen py-3 text-center text-sm font-bold text-primary-foreground transition hover:opacity-90 active:scale-[0.99]" @click="close">Hemen Kirala</button>
                        </div>
                    </div>
                </Transition>
            </div>
        </div>
    </Transition>
</template>
